import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Breadcrumbs,
  Link,
  CircularProgress,
  Collapse,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import CategoryDialog from './components/knowledge/CategoryDialog';
import PageDialog from './components/knowledge/PageDialog';
import PageViewer from './components/knowledge/PageViewer';
import SearchResults from './components/knowledge/SearchResults';

function KnowledgeBaseApp() {
  const { token, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [categories, setCategories] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  
  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Fetch categories and pages
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch top-level categories
        const categoriesResponse = await fetch(`${config.apiUrl}/knowledgebase/categories?parentId=null`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!categoriesResponse.ok) {
          throw new Error('Failed to fetch categories');
        }
        
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);
        
        // Fetch all pages
        const pagesResponse = await fetch(`${config.apiUrl}/knowledgebase/pages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!pagesResponse.ok) {
          throw new Error('Failed to fetch pages');
        }
        
        const pagesData = await pagesResponse.json();
        setPages(pagesData);
        
        setError('');
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load content. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);
  
  // Fetch subcategories when a category is selected
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!selectedCategory) return;
      
      try {
        const response = await fetch(`${config.apiUrl}/knowledgebase/categories?parentId=${selectedCategory._id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch subcategories');
        }
        
        const subcategoriesData = await response.json();
        
        // Update the selected category with subcategories
        setSelectedCategory(prev => ({
          ...prev,
          subcategories: subcategoriesData
        }));
        
        // Fetch pages for this category
        const pagesResponse = await fetch(`${config.apiUrl}/knowledgebase/pages?categoryId=${selectedCategory._id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!pagesResponse.ok) {
          throw new Error('Failed to fetch category pages');
        }
        
        const categoryPagesData = await pagesResponse.json();
        setPages(categoryPagesData);
        
      } catch (err) {
        console.error('Error fetching subcategories:', err);
      }
    };
    
    fetchSubcategories();
  }, [selectedCategory?._id, token]);
  
  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedPage(null);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    
    // Close mobile drawer if open
    if (isMobile) {
      setDrawerOpen(false);
    }
  };
  
  // Handle page selection
  const handlePageSelect = async (page) => {
    try {
      const response = await fetch(`${config.apiUrl}/knowledgebase/pages/${page._id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch page details');
      }
      
      const pageData = await response.json();
      setSelectedPage(pageData);
      
      // Close mobile drawer if open
      if (isMobile) {
        setDrawerOpen(false);
      }
    } catch (err) {
      console.error('Error fetching page:', err);
      setError('Failed to load page content. Please try again later.');
    }
  };
  
  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };
  
  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setSelectedCategory(null);
    setSelectedPage(null);
    
    try {
      const response = await fetch(`${config.apiUrl}/knowledgebase/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Search failed');
      }
      
      const results = await response.json();
      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
    }
  };
  
  // Handle category creation/update
  const handleSaveCategory = async (categoryData) => {
    try {
      const url = editingCategory 
        ? `${config.apiUrl}/knowledgebase/categories/${editingCategory._id}`
        : `${config.apiUrl}/knowledgebase/categories`;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(categoryData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingCategory ? 'update' : 'create'} category`);
      }
      
      // Refresh categories
      const categoriesResponse = await fetch(`${config.apiUrl}/knowledgebase/categories?parentId=null`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const categoriesData = await categoriesResponse.json();
      setCategories(categoriesData);
      
      setCategoryDialogOpen(false);
      setEditingCategory(null);
    } catch (err) {
      console.error('Error saving category:', err);
      setError(`Failed to ${editingCategory ? 'update' : 'create'} category. Please try again.`);
    }
  };
  
  // Handle page creation/update
  const handleSavePage = async (pageData) => {
    try {
      const url = editingPage 
        ? `${config.apiUrl}/knowledgebase/pages/${editingPage._id}`
        : `${config.apiUrl}/knowledgebase/pages`;
      
      const method = editingPage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(pageData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingPage ? 'update' : 'create'} page`);
      }
      
      const savedPage = await response.json();
      
      // Refresh pages for the current category
      if (selectedCategory) {
        const pagesResponse = await fetch(`${config.apiUrl}/knowledgebase/pages?categoryId=${selectedCategory._id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const pagesData = await pagesResponse.json();
        setPages(pagesData);
      } else {
        // Refresh all pages
        const pagesResponse = await fetch(`${config.apiUrl}/knowledgebase/pages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const pagesData = await pagesResponse.json();
        setPages(pagesData);
      }
      
      // If we just created or updated the page, select it
      if (editingPage) {
        setSelectedPage(savedPage);
      }
      
      setPageDialogOpen(false);
      setEditingPage(null);
    } catch (err) {
      console.error('Error saving page:', err);
      setError(`Failed to ${editingPage ? 'update' : 'create'} page. Please try again.`);
    }
  };
  
  // Render category tree
  const renderCategoryTree = (categoryList, level = 0) => {
    return categoryList.map(category => {
      const hasSubcategories = category.subcategories && category.subcategories.length > 0;
      const isExpanded = expandedCategories[category._id];
      
      return (
        <React.Fragment key={category._id}>
          <ListItem
            button
            onClick={() => handleCategorySelect(category)}
            selected={selectedCategory?._id === category._id}
            sx={{ pl: level * 2 + 2 }}
          >
            <ListItemIcon>
              {hasSubcategories ? (isExpanded ? <FolderOpenIcon /> : <FolderIcon />) : <FolderIcon />}
            </ListItemIcon>
            <ListItemText primary={category.name} />
            {hasSubcategories && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCategoryExpansion(category._id);
                }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </ListItem>
          
          {hasSubcategories && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {renderCategoryTree(category.subcategories, level + 1)}
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  };
  
  // Render pages list
  const renderPagesList = (pagesList) => {
    return pagesList.map(page => (
      <ListItem
        button
        key={page._id}
        onClick={() => handlePageSelect(page)}
        selected={selectedPage?._id === page._id}
        sx={{ pl: 2 }}
      >
        <ListItemIcon>
          <DescriptionIcon />
        </ListItemIcon>
        <ListItemText primary={page.title} />
      </ListItem>
    ));
  };
  
  // Render breadcrumbs
  const renderBreadcrumbs = () => {
    const items = [];
    
    items.push(
      <Link
        key="home"
        color="inherit"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          setSelectedCategory(null);
          setSelectedPage(null);
          setSearchQuery('');
          setSearchResults([]);
          setIsSearching(false);
        }}
      >
        Home
      </Link>
    );
    
    if (selectedCategory) {
      items.push(
        <Typography key="category" color="text.primary">
          {selectedCategory.name}
        </Typography>
      );
    }
    
    if (selectedPage) {
      items.push(
        <Typography key="page" color="text.primary">
          {selectedPage.title}
        </Typography>
      );
    }
    
    if (isSearching) {
      items.push(
        <Typography key="search" color="text.primary">
          Search Results
        </Typography>
      );
    }
    
    return (
      <Breadcrumbs separator="›" aria-label="breadcrumb">
        {items}
      </Breadcrumbs>
    );
  };
  
  // Sidebar content
  const sidebarContent = (
    <>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search knowledge base..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          size="small"
          sx={{ mb: 2 }}
        />
        
        {user?.isAdmin && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => {
                setEditingCategory(null);
                setCategoryDialogOpen(true);
              }}
              fullWidth
            >
              New Category
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => {
                setEditingPage(null);
                setPageDialogOpen(true);
              }}
              fullWidth
            >
              New Page
            </Button>
          </Box>
        )}
      </Box>
      
      <Divider />
      
      <List component="nav" aria-label="categories" sx={{ flex: 1, overflow: 'auto' }}>
        {categories.length > 0 ? (
          renderCategoryTree(categories)
        ) : (
          <ListItem>
            <ListItemText primary="No categories found" />
          </ListItem>
        )}
      </List>
      
      {selectedCategory && (
        <>
          <Divider />
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, bgcolor: 'grey.100' }}>
            Pages in {selectedCategory.name}
          </Typography>
          <List component="nav" aria-label="pages" sx={{ overflow: 'auto' }}>
            {pages.length > 0 ? (
              renderPagesList(pages)
            ) : (
              <ListItem>
                <ListItemText primary="No pages found in this category" />
              </ListItem>
            )}
          </List>
        </>
      )}
    </>
  );
  
  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Mobile app bar */}
      {isMobile && (
        <AppBar position="fixed" color="default" elevation={1} sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setDrawerOpen(!drawerOpen)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              Knowledge Base
            </Typography>
          </Toolbar>
        </AppBar>
      )}
      
      {/* Sidebar - responsive drawer on mobile */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            width: 280,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              top: '64px',
              height: 'calc(100% - 64px)',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {sidebarContent}
        </Box>
      )}
      
      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          overflow: 'auto',
          mt: isMobile ? 8 : 0,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper sx={{ p: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
            <Typography>{error}</Typography>
          </Paper>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              {renderBreadcrumbs()}
            </Box>
            
            {isSearching ? (
              <SearchResults 
                results={searchResults} 
                query={searchQuery} 
                onSelectPage={handlePageSelect} 
              />
            ) : selectedPage ? (
              <PageViewer 
                page={selectedPage} 
                onEdit={user?.isAdmin ? () => {
                  setEditingPage(selectedPage);
                  setPageDialogOpen(true);
                } : undefined} 
              />
            ) : selectedCategory ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h4">{selectedCategory.name}</Typography>
                  {user?.isAdmin && (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setEditingCategory(selectedCategory);
                        setCategoryDialogOpen(true);
                      }}
                    >
                      Edit Category
                    </Button>
                  )}
                </Box>
                
                {selectedCategory.description && (
                  <Typography variant="body1" paragraph>
                    {selectedCategory.description}
                  </Typography>
                )}
                
                <Typography variant="h6" sx={{ mb: 2 }}>Pages</Typography>
                
                <Grid container spacing={2}>
                  {pages.length > 0 ? (
                    pages.map(page => (
                      <Grid item xs={12} sm={6} md={4} key={page._id}>
                        <Paper
                          sx={{
                            p: 2,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 3 },
                          }}
                          onClick={() => handlePageSelect(page)}
                        >
                          <Typography variant="h6" gutterBottom>
                            {page.title}
                          </Typography>
                          {page.tags && page.tags.length > 0 && (
                            <Box sx={{ mt: 'auto', pt: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Tags: {page.tags.join(', ')}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, bgcolor: 'grey.100' }}>
                        <Typography>No pages found in this category.</Typography>
                        {user?.isAdmin && (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{ mt: 2 }}
                            onClick={() => {
                              setEditingPage(null);
                              setPageDialogOpen(true);
                            }}
                          >
                            Create Page
                          </Button>
                        )}
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ) : (
              <Box>
                <Typography variant="h4" gutterBottom>Knowledge Base</Typography>
                <Typography paragraph>
                  Welcome to the Knowledge Base. Browse categories and pages to find helpful information.
                </Typography>
                
                <Typography variant="h6" sx={{ mb: 2 }}>Categories</Typography>
                
                <Grid container spacing={2}>
                  {categories.length > 0 ? (
                    categories.map(category => (
                      <Grid item xs={12} sm={6} md={4} key={category._id}>
                        <Paper
                          sx={{
                            p: 2,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 3 },
                          }}
                          onClick={() => handleCategorySelect(category)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="h6">
                              {category.name}
                            </Typography>
                          </Box>
                          
                          {category.description && (
                            <Typography variant="body2" color="text.secondary">
                              {category.description}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, bgcolor: 'grey.100' }}>
                        <Typography>No categories found.</Typography>
                        {user?.isAdmin && (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{ mt: 2 }}
                            onClick={() => {
                              setEditingCategory(null);
                              setCategoryDialogOpen(true);
                            }}
                          >
                            Create Category
                          </Button>
                        )}
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Box>
      
      {/* Dialogs */}
      <CategoryDialog
        open={categoryDialogOpen}
        onClose={() => {
          setCategoryDialogOpen(false);
          setEditingCategory(null);
        }}
        onSave={handleSaveCategory}
        category={editingCategory}
        categories={categories}
      />
      
      <PageDialog
        open={pageDialogOpen}
        onClose={() => {
          setPageDialogOpen(false);
          setEditingPage(null);
        }}
        onSave={handleSavePage}
        page={editingPage}
        categories={categories}
        selectedCategory={selectedCategory}
      />
    </Box>
  );
}

export default KnowledgeBaseApp; 